import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import useTrialExpirationHandler from './useTrialExpirationHandler';
import { useAlert, AlertMessage } from './useAlert';
import { useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export interface SimulationData {
  id: string;
  content: string;
  platform: string;
  sender: string;
  sector: string;
  resultJson: any;
  imageUrl?: string;
  isPrivate: boolean;
  createdBy: string;
  status?: 'queued' | 'processing' | 'completed' | 'failed';
  progress?: number | null;
  processingError?: string | null;
  ideaDraft?: {
    id: string;
    draftNumber: number;
    idea: {
      id: string;
    };
  };
}

export interface UseSimulationResultsReturn {
  loading: boolean;
  simulation: SimulationData | null;
  effectiveSimId: string | null;
  isCreator: boolean;
  error: string | null;
  // Trial expiration handling
  isExpiredModalOpen: boolean;
  isAdmin: boolean;
  closeExpiredModal: () => void;
  // Alert handling
  alertMessage: any;
  showAlert: (alert: AlertMessage) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  hideAlert: () => void;
  // Actions
  refetchSimulation: () => Promise<void>;
  updateSimulation: (updates: Partial<SimulationData>) => void;
}

export const useSimulationResults = (): UseSimulationResultsReturn => {
  const navigate = useNavigate();
  const { id: urlSimId } = useParams<{ id: string }>();
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [simulation, setSimulation] = useState<SimulationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Extract simulation ID from URL or localStorage
  const lastSimId = localStorage.getItem('lastSimulationId');
  const effectiveSimId = urlSimId || lastSimId;

  // Trial expiration handling
  const { 
    isExpiredModalOpen, 
    isAdmin, 
    handleApiError, 
    closeExpiredModal 
  } = useTrialExpirationHandler();

  // Alert handling
  const { alertMessage, showAlert, showSuccess, showError, hideAlert } = useAlert();
  const prevStatusRef = useRef<string | null>(null);

  // Check if user is the creator of the simulation
  const isCreator = simulation?.createdBy === user?.id;

  // Fetch simulation data
  const fetchSimulationData = async (opts?: { silent?: boolean }) => {
    const silent = !!opts?.silent;
    if (!effectiveSimId || effectiveSimId === 'undefined' || effectiveSimId === 'null') {
      setLoading(false);
      return;
    }

    if (!isAuthenticated) {
      return;
    }

    if (!silent) {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await axios.get(`${API_URL}/api/simulation/${effectiveSimId}`, {
        withCredentials: true
      });
      
      setSimulation(response.data.simulation);
    } catch (error) {
      if (!handleApiError(error)) { 
        let errorMessage = 'Failed to load simulation results';
        if (axios.isAxiosError(error)) {
          errorMessage = error.response?.data?.error || error.message || errorMessage;
        }
        setError(errorMessage);
        showError(errorMessage);
        setSimulation(null); 
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Update simulation state
  const updateSimulation = (updates: Partial<SimulationData>) => {
    setSimulation(prev => prev ? { ...prev, ...updates } : null);
  };

  // Effect 1: Authentication Check
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Effect 2: Fetch simulation data
  useEffect(() => {
    fetchSimulationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [effectiveSimId, isAuthenticated]);

  // Effect 2b: Poll while processing
  useEffect(() => {
    if (!simulation) return;
    const needsPoll = simulation.status === 'queued' || simulation.status === 'processing' || !simulation.resultJson;
    if (!needsPoll) return;
    const interval = setInterval(() => {
      fetchSimulationData({ silent: true });
    }, 5000);
    return () => clearInterval(interval);
  }, [simulation?.status, simulation?.id]);

  // Effect 3: Handle URL synchronization and redirection
  useEffect(() => {
    if (loading || !isAuthenticated) {
      return;
    }

    // Case 1: URL Sync - ID from localStorage, but not in URL. Update URL first.
    if (effectiveSimId && !urlSimId && lastSimId) {
      navigate(`/simulations/${lastSimId}/results`, { replace: true });
      return;
    }

    // Case 2: No ID anywhere (URL or local storage). Should redirect to dashboard.
    if (!effectiveSimId || effectiveSimId === 'undefined' || effectiveSimId === 'null') {
      navigate('/dashboard', { replace: true });
      return;
    }
    
    // Case 3: Has an ID, loading is false, but simulation is still null (fetch might have failed or returned null)
    // AND no specific error alert has been set by the fetch process to explain why.
    if (effectiveSimId && !simulation && !alertMessage) {
      navigate('/dashboard', { replace: true });
      return;
    }
  }, [loading, simulation, isAuthenticated, effectiveSimId, urlSimId, lastSimId, navigate, alertMessage]);

  // Effect 4: Notify on status transitions
  useEffect(() => {
    const current = simulation?.status || null;
    const prev = prevStatusRef.current;
    if (prev && prev !== current) {
      if (current === 'completed') {
        showSuccess('Simulation is ready.');
      } else if (current === 'failed') {
        showError('Simulation failed to process.');
      }
    }
    prevStatusRef.current = current;
  }, [simulation?.status]);

  return {
    loading,
    simulation,
    effectiveSimId,
    isCreator,
    error,
    // Trial expiration handling
    isExpiredModalOpen,
    isAdmin,
    closeExpiredModal,
    // Alert handling
    alertMessage,
    showAlert,
    showSuccess,
    showError,
    hideAlert,
    // Actions
    refetchSimulation: fetchSimulationData,
    updateSimulation
  };
};
